import { Text, VSpacer } from '@/components/DesignSystem';
import {
  SubwalletBalanceModalBottomSheet,
} from '@/components/shared/SubwalletBalance/SubwalletBalanceModalBottomSheet';
import { SubwalletCard } from '@/components/shared/SubwalletBalance/SubwalletCard';
import {
  groupWalletBalances,
  programYearForGroup,
} from '@/components/shared/SubwalletBalance/walletCardGrouping';
import { QueryKeys } from '@/constants/QueryKeys';
import { LoyaltySkuApi } from '@/utilities/api/LoyaltySkuApi';
import { UserApi } from '@/utilities/api/UserApi';
import { ApiUser, ResolvedApiSubwalletBalance } from '@api/interfaces';
import { CircularProgress, Divider, Stack } from '@mui/material';
import { calculateUnexpiredPoints, isBalanceLocked } from '@shared/utilities';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface SubwalletListProps {
  accessToken?: string,
  brandColor?: string,
  customer: ApiUser,
  hideBorder?: boolean,
  hideLoyaltySkus?: boolean,
  subwalletBalances?: ResolvedApiSubwalletBalance[],
}

export const SubwalletList = ({
  accessToken,
  brandColor,
  customer,
  hideBorder,
  hideLoyaltySkus = false,
  subwalletBalances,
}: SubwalletListProps) => {
  const { t } = useTranslation();
  const [selectedBalanceGroup, setSelectedBalanceGroup] =
    useState<ResolvedApiSubwalletBalance[]>([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const { data, isFetching } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, customer.id],
    () => UserApi.getSubwalletBalances(customer.id),
    { enabled: !subwalletBalances },
  );

  const { data: loyaltySkus } = useQuery(
    [QueryKeys.GET_LOYALTY_SKUS, accessToken, customer.retailerId],
    () => LoyaltySkuApi.list({ accessToken, retailerId: customer.retailerId! }),
    { enabled: !!customer.retailerId && !hideLoyaltySkus },
  );

  const balances = subwalletBalances ?? data;

  // Locked points are not available to spend, so they are shown above this list by
  // LockedSubwalletCards rather than among the wallets someone can redeem from.
  const groupedBalances = useMemo(() => (
    groupWalletBalances(balances).filter((group) => !isBalanceLocked(group[0]))
  ), [balances]);

  if (isFetching) {
    return (
      <Stack alignItems="center" gap={2}>
        <CircularProgress />
      </Stack>
    );
  }

  if (groupedBalances.every((g) => g.reduce((acc, b) => acc + b.points, 0) === 0)) {
    return (
      <Stack gap={2}>
        <Divider />
        <Stack alignItems="center">
          <VSpacer size="10" />
          <Text category="body-xlarge">
            Your wallet is empty
          </Text>
          <VSpacer size="5" />
          <Text category="body-medium">
            Looks like there are no loyalty points in your wallet at the moment.
            Place an order today to start earning!
          </Text>
          <VSpacer size="12" />
        </Stack>
      </Stack>
    );
  }

  const hasNonZeroBalance = (balances: ResolvedApiSubwalletBalance[]) => (
    calculateUnexpiredPoints(balances) !== 0
  );
  const nonZeroGroupedBalances = groupedBalances.filter(hasNonZeroBalance);

  const filterLoyaltySkus = (subwalletId: string) => (
    loyaltySkus?.filter((sku) => sku.subwalletId === subwalletId) ?? []
  );

  const getTitle = (group: ResolvedApiSubwalletBalance[]) => {
    const year = programYearForGroup(group);
    return year
      ? t($ => $.wallet.programYearName, { name: group[0].subwallet.name, year })
      : undefined;
  };

  return (
    <Stack gap="16px">
      {nonZeroGroupedBalances.map((balances, idx) => (
        <SubwalletCard
          balances={balances}
          hideBorder={hideBorder}
          key={idx}
          loyaltySkus={filterLoyaltySkus(balances[0].subwalletId)}
          onClick={() => {
            setSelectedBalanceGroup(balances);
            setShowDetailsModal(true);
          }}
          title={getTitle(balances)}
        />
      ))}
      {showDetailsModal && (
        <SubwalletBalanceModalBottomSheet
          accessToken={accessToken}
          balances={selectedBalanceGroup}
          brandColor={brandColor}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </Stack>
  );
};
